import { useCallback, useEffect, useState } from 'react';

import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming';
import { Provider } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import { setLocale } from 'yup';

import { getUserProfile, refreshUserProfile } from 'helpers/userProfile';
import { CloseIcon } from 'icons';
import { backendDescriptionToShow } from 'providers';
import { Routes } from 'routes';
import store from 'store';
import { useStateVar } from './helpers/useStateVar';
import MenuContext from './contexts/menuContext';
import i18n from './i18n/index';
import useGaTracker from './useGaTracker';
import moment from 'moment';
import 'sanitize.css/sanitize.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import 'antd/dist/antd.min.css';
import 'assets/css/font-awesome.min.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { CardProvider } from './contexts/CardContext';
import { TourProvider } from './contexts/TourContext';
import posthog from 'posthog-js';
import { identifyUser } from './helpers/posthogHelper';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';
import { GlobalStyles } from 'styles/GlobalStyles';
import { NotificationPopUpProvider } from './contexts/notificationPopUp';
import { getKeycloakData } from './helpers/auth';
import Hotjar from '@hotjar/browser';
import { HotjarRouteListener, identifyUserHJ } from './helpers/hotjarHelper';
import SelectPlataformContext from './contexts/selectPlataform';
import { ToastProvider } from './contexts/newToastContext';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { colors } from './styles/colors';

const ReFlexbox = {
  breakpoints: ['360px', '480px', '768px', '992px', '1200px'],
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.brand.primary.base,
      light: colors.brand.primary.light,
      dark: colors.brand.primary.dark,
    },
  },
});

export const App = (): JSX.Element => {
  const auth = useAuth();
  const { isAuthenticated } = auth;
  const history = useHistory();
  useGaTracker();
  const isOpenMenu = localStorage.getItem('menuDiel') && localStorage.getItem('menuDiel') === 'false';
  const selectPlataformStorage = localStorage.getItem('choosePlataform') || 'analise/unidades';
  const [menuToogle, setMenuToogle] = useState(isOpenMenu === null ? true : isOpenMenu === false);
  const [selectPlataform, setSelectPlataform] = useState(selectPlataformStorage);

  moment.locale(i18n.language === 'pt' ? 'pt-BR' : 'en');
  const [state, _render, setState] = useStateVar({
    checkingAuthToken: true,
  });

  if (process.env.REACT_APP_HOTJAR_ID && process.env.REACT_APP_HOTJAR_VERSION) {
    const hotjarId = Number(process.env.REACT_APP_HOTJAR_ID);
    const hotjarVersion = Number(process.env.REACT_APP_HOTJAR_VERSION);
    if (!Number.isNaN(hotjarId) && !Number.isNaN(hotjarVersion)) {
      Hotjar.init(hotjarId, hotjarVersion);
    }
  }

  if (process.env.REACT_APP_POSTHOG_HOST && process.env.REACT_APP_POSTHOG_KEY) {
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST,
      disable_session_recording: true,
    });
  }
  setLocale({
    mixed: { required: 'Campo obrigatório' },
  });

  const fetchUserProfile = useCallback(async () => {
    setState({ checkingAuthToken: true });
    try {
      const user = getUserProfile();
      identifyUser(user);
      identifyUserHJ(user);
      await refreshUserProfile();

      i18n.changeLanguage(user && user.prefsObj && user.prefsObj.language
        ? user.prefsObj.language : 'pt');
      if (user.isTracker) {
        posthog.startSessionRecording();
      }
    } catch (err: any) {
      console.log(err);

      // Se o erro for o backend informando explicitamente que o token é inválido, tira ele do localStorage.
      if (err?.response?.status === 401) {
        console.log('Token inválido, removendo do localStorage');
        history.push('/logout');
      }
    }
    setState({ checkingAuthToken: false });
  }, [setState, history]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchUserProfile();
  }, [isAuthenticated, fetchUserProfile]);

  useEffect(() => {
    const callback = async () => {
      await auth.startSilentRenew();
      const user = getKeycloakData();
      if (user) {
        localStorage.setItem('@diel:token', user.access_token);
      }
    };
    auth.events.addAccessTokenExpiring(callback);

    return () => {
      auth.events.removeAccessTokenExpiring(callback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.events, auth.signinSilent]);

  switch (auth.activeNavigator) {
    case 'signinSilent':
      return <div>Signing you in...</div>;
    case 'signoutRedirect':
      return <div>Signing you out...</div>;
  }

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    return (
      <div>
        Oops...
        {auth.error.message}
      </div>
    );
  }

  return (
    <MenuContext.Provider value={{ menuToogle, setMenuToogle }}>
      <SelectPlataformContext.Provider value={{ selectPlataform, setSelectPlataform }}>
        <HotjarRouteListener enabled />
        <Provider store={store}>
          <TourProvider>
            <CardProvider>
              <NotificationPopUpProvider>
                <ToastProvider>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                      <EmotionThemeProvider theme={ReFlexbox}>
                        {(!!backendDescriptionToShow) && (
                        <div
                          style={{
                            fontSize: '10px',
                            backgroundColor: '#00ff9b',
                            color: '#090b4b',
                            padding: '4px 3px',
                            fontWeight: 'bold',
                            position: 'absolute',
                            zIndex: 1001,
                          }}
                        >
                          {backendDescriptionToShow}
                        </div>
                        )}
                        {
                      state.checkingAuthToken && isAuthenticated
                        ? <div>&nbsp;</div>
                        : <Routes />
                    }
                        <GlobalStyles />
                        <ToastContainer
                          autoClose={5000}
                          hideProgressBar
                          newestOnTop={false}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                          toastClassName="toastify-body"
                          position={toast.POSITION.TOP_RIGHT}
                          closeButton={<CloseIcon size="14" />}
                        />
                      </EmotionThemeProvider>
                    </ThemeProvider>
                  </StyledEngineProvider>
                </ToastProvider>
              </NotificationPopUpProvider>
            </CardProvider>
          </TourProvider>
        </Provider>
      </SelectPlataformContext.Provider>
    </MenuContext.Provider>
  );
};
